import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Button,
    Collapse,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    AccessTimeRounded,
    ArrowForwardRounded,
    CalendarMonthOutlined,
    GroupOutlined,
    VisibilityOffOutlined,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { DIALOG_ACCEPT_BUTTON_SX } from "../common/dialogActionButtonSx";
import TooltipComponent from "../common/Tooltip";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import { useLobbyMatchNight } from "./LobbyMatchNightContext";

const MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

const SECONDARY_ACTION_SX = {
    height: "2.35rem",
    minWidth: 0,
    px: "1rem",
    borderRadius: "9px",
    border: `1px solid ${alpha(colors.accent.main, 0.2)}`,
    color: colors.text.secondary,
    background: alpha("#fff", 0.025),
    fontSize: "0.66rem",
    fontWeight: 650,
    letterSpacing: "0.07em",
    lineHeight: 1,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    transition:
        "color 150ms ease, border-color 150ms ease, background-color 150ms ease",
    "&:hover": {
        color: colors.text.primary,
        borderColor: alpha(colors.accent.main, 0.36),
        backgroundColor: alpha(colors.primary.main, 0.065),
    },
    [MOBILE_MQ]: {
        height: "2.25rem",
        px: "0.8rem",
        fontSize: "0.61rem",
    },
};

const META_PILL_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    minWidth: 0,
    px: "0.6rem",
    py: "0.35rem",
    borderRadius: "999px",
    color: colors.text.secondary,
    backgroundColor: alpha("#fff", 0.025),
    border: `1px solid ${colors.border.subtle}`,
    fontSize: "0.68rem",
    fontWeight: 550,
    lineHeight: 1,
};

export default function LobbyMatchNightPanel() {
    const {
        available,
        hidden,
        registered,
        dateLabel,
        countdownLabel,
        countsLabel,
        statusLabel,
        cancelError,
        cancelling,
        hide,
        reserve,
        cancelRsvp,
    } = useLobbyMatchNight();

    if (!available) return null;

    return (
        <Collapse in={!hidden} unmountOnExit timeout={240}>
            <Box
                component="section"
                aria-label="Upcoming match night"
                sx={{
                    position: "relative",
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "auto minmax(0, 1fr) auto",
                        md: "auto minmax(0, 1fr) auto",
                    },
                    alignItems: "center",
                    columnGap: { xs: "0.8rem", md: "1rem" },
                    rowGap: "0.75rem",
                    width: "100%",
                    mb: { xs: "0.75rem", md: "0.9rem" },
                    px: { xs: "0.85rem", sm: "1rem", xl: "1.15rem" },
                    py: { xs: "0.8rem", sm: "0.9rem", xl: "1rem" },
                    overflow: "hidden",
                    boxSizing: "border-box",
                    borderRadius: { xs: "10px", md: "12px" },
                    border: `1px solid ${alpha(colors.accent.main, 0.1)}`,
                    color: colors.text.primary,
                    background: `
                        radial-gradient(circle at 16% 0%, ${alpha(colors.primary.main, 0.105)}, transparent 34%),
                        linear-gradient(110deg, rgba(18,16,13,0.98) 0%, rgba(10,10,12,0.99) 48%, rgba(8,8,10,1) 100%)
                    `,
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.035),
                        0 14px 34px rgba(0,0,0,0.24)
                    `,
                    [MOBILE_MQ]: {
                        gridTemplateColumns: "auto minmax(0, 1fr)",
                        alignItems: "start",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        display: "grid",
                        placeItems: "center",
                        width: { xs: "2.6rem", md: "3rem" },
                        height: { xs: "2.6rem", md: "3rem" },
                        flexShrink: 0,
                        borderRadius: "10px",
                        color: colors.accent.main,
                        background: `linear-gradient(145deg, ${alpha(colors.primary.main, 0.17)}, ${alpha(colors.primary.main, 0.055)})`,
                        border: `1px solid ${alpha(colors.accent.main, 0.2)}`,
                        boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.06)}, 0 8px 24px ${alpha(colors.primary.main, 0.08)}`,
                    }}
                >
                    <CalendarMonthOutlined
                        sx={{ fontSize: { xs: "1.15rem", md: "1.3rem" } }}
                    />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.55rem",
                            flexWrap: "wrap",
                            mb: "0.3rem",
                        }}
                    >
                        <Typography
                            sx={{
                                color: alpha(colors.accent.main, 0.85),
                                fontSize: "0.61rem",
                                fontWeight: 750,
                                letterSpacing: "0.17em",
                                lineHeight: 1,
                                textTransform: "uppercase",
                            }}
                        >
                            Ouro match night
                        </Typography>
                        {registered ? (
                            <Typography
                                component="span"
                                sx={{
                                    px: "0.45rem",
                                    py: "0.22rem",
                                    borderRadius: "999px",
                                    color: "#77D99A",
                                    backgroundColor: alpha(
                                        colors.state.success.main,
                                        0.08,
                                    ),
                                    border: `1px solid ${alpha(colors.state.success.main, 0.15)}`,
                                    fontSize: "0.57rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    lineHeight: 1,
                                    textTransform: "uppercase",
                                }}
                            >
                                {statusLabel}
                            </Typography>
                        ) : null}
                    </Box>

                    <Typography
                        sx={{
                            overflow: "hidden",
                            color: colors.text.primary,
                            fontSize: { xs: "0.88rem", md: "0.98rem" },
                            fontWeight: 700,
                            letterSpacing: "0.01em",
                            lineHeight: 1.25,
                            textOverflow: "ellipsis",
                            whiteSpace: { xs: "normal", sm: "nowrap" },
                        }}
                    >
                        {dateLabel}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.45rem",
                            flexWrap: "wrap",
                            mt: "0.55rem",
                        }}
                    >
                        <Box sx={META_PILL_SX}>
                            <AccessTimeRounded
                                sx={{
                                    flexShrink: 0,
                                    color: alpha(colors.accent.main, 0.72),
                                    fontSize: "0.82rem",
                                }}
                            />
                            {countdownLabel}
                        </Box>
                        <Box sx={META_PILL_SX}>
                            <GroupOutlined
                                sx={{
                                    flexShrink: 0,
                                    color: colors.text.muted,
                                    fontSize: "0.82rem",
                                }}
                            />
                            {countsLabel}
                        </Box>
                    </Box>

                    {cancelError ? (
                        <Typography
                            sx={{
                                mt: "0.45rem",
                                color: "#F1A3A3",
                                fontSize: "0.67rem",
                                lineHeight: 1.35,
                            }}
                        >
                            {cancelError}
                        </Typography>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                        [MOBILE_MQ]: {
                            gridColumn: "1 / -1",
                            width: "100%",
                            justifyContent: "stretch",
                            pt: "0.15rem",
                            "& > *": {
                                flex: "1 1 auto",
                            },
                        },
                    }}
                >
                    {registered ? (
                        <>
                            <Button
                                component={RouterLink}
                                to="/match-night"
                                endIcon={
                                    <ArrowForwardRounded
                                        sx={{ fontSize: "0.85rem !important" }}
                                    />
                                }
                                sx={SECONDARY_ACTION_SX}
                            >
                                View details
                            </Button>
                            <Button
                                disabled={cancelling}
                                onClick={cancelRsvp}
                                sx={{
                                    ...SECONDARY_ACTION_SX,
                                    color: alpha("#F2A0A0", 0.82),
                                    borderColor: alpha(
                                        colors.state.danger.main,
                                        0.2,
                                    ),
                                    "&:hover": {
                                        color: "#F5B2B2",
                                        borderColor: alpha(
                                            colors.state.danger.main,
                                            0.34,
                                        ),
                                        backgroundColor: alpha(
                                            colors.state.danger.main,
                                            0.07,
                                        ),
                                    },
                                }}
                            >
                                {cancelling ? "Cancelling..." : "Cancel RSVP"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={reserve}
                                sx={{
                                    ...DIALOG_ACCEPT_BUTTON_SX,
                                    height: "2.35rem",
                                    minWidth: "7.4rem",
                                    px: "1rem",
                                    borderRadius: "9px",
                                    fontSize: "0.67rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    [MOBILE_MQ]: {
                                        height: "2.25rem",
                                        minWidth: 0,
                                        fontSize: "0.62rem",
                                    },
                                }}
                            >
                                Reserve spot
                            </Button>
                            <Button
                                component={RouterLink}
                                to="/match-night"
                                endIcon={
                                    <ArrowForwardRounded
                                        sx={{ fontSize: "0.85rem !important" }}
                                    />
                                }
                                sx={SECONDARY_ACTION_SX}
                            >
                                All nights
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={hide}
                        startIcon={
                            <VisibilityOffOutlined
                                sx={{ fontSize: "0.85rem !important" }}
                            />
                        }
                        sx={{
                            ...SECONDARY_ACTION_SX,
                            px: "0.75rem",
                            color: colors.text.muted,
                            borderColor: colors.border.subtle,
                            backgroundColor: "transparent",
                            [MOBILE_MQ]: {
                                flex: "0 1 auto !important",
                            },
                        }}
                    >
                        Hide
                    </Button>
                </Box>
            </Box>
        </Collapse>
    );
}

export function LobbyMatchNightShowButton() {
    const { available, hidden, show } = useLobbyMatchNight();

    if (!available || !hidden) return null;

    return (
        <TooltipComponent
            title="Scheduled CS2 sessions where players reserve a spot and play together."
            placement="bottom"
            enterTouchDelay={0}
            leaveTouchDelay={2500}
        >
            <Button
                onClick={show}
                aria-label="Show match nights"
                sx={{
                    height: { xs: "2.2rem", xl: "2.35rem" },
                    minWidth: 0,
                    px: { xs: "0.75rem", xl: "0.9rem" },
                    borderRadius: "8px",
                    color: colors.text.secondary,
                    background: "linear-gradient(180deg, rgba(19,20,23,0.94), rgba(12,13,15,0.96))",
                    border: `1px solid ${colors.border.subtle}`,
                    boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.025)}, 0 4px 12px rgba(0,0,0,0.16)`,
                    fontSize: { xs: "0.6rem", xl: "0.64rem" },
                    fontWeight: 650,
                    letterSpacing: { xs: "0.045em", xl: "0.055em" },
                    lineHeight: 1,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    transition:
                        "color 150ms ease, border-color 150ms ease, background 150ms ease, box-shadow 150ms ease",
                    "&:hover": {
                        color: colors.text.primary,
                        borderColor: colors.border.default,
                        background: "linear-gradient(180deg, rgba(24,25,29,0.96), rgba(15,16,19,0.98))",
                        boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.035)}, 0 5px 14px rgba(0,0,0,0.2)`,
                    },
                    "&.Mui-focusVisible": {
                        outline: `2px solid ${alpha(colors.accent.main, 0.35)}`,
                        outlineOffset: "2px",
                    },
                    [MOBILE_MQ]: {
                        height: "auto",
                        minHeight: 0,
                        minWidth: 0,
                        flex: "0 0 auto",
                        alignSelf: "center",
                        px: "0.55rem",
                        py: "0.675rem",
                        borderRadius: "8px",
                        fontSize: "0.62rem",
                        letterSpacing: "0.06em",
                        lineHeight: 1.15,
                    },
                }}
            >
                <Box
                    component="span"
                    sx={{
                        [MOBILE_MQ]: {
                            display: "none",
                        },
                    }}
                >
                    Show match nights
                </Box>
                <Box
                    component="span"
                    sx={{
                        display: "none",
                        [MOBILE_MQ]: {
                            display: "inline",
                        },
                    }}
                >
                    Nights
                </Box>
            </Button>
        </TooltipComponent>
    );
}
